// (c) chaehongee. Confidential and proprietary.

import type { User, Session } from '@supabase/supabase-js'
import { createContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from "@sentry/react"
import { getSupabaseClient } from '../services/supabase'
import { checkUserEmail } from '../services/user'
import { analytics } from '../services/analytics'
import { telemetry } from '../services/telemetry'
import { SessionExpiredModal } from '../components/Auth/SessionExpiredModal'

export interface AuthContextValue {
    user: User | null
    session: Session | null
    loading: boolean
    signInWithGoogle: (returnUrl?: string) => Promise<void>
    signInWithApple: (returnUrl?: string) => Promise<void>
    signInWithEmail: (email: string, password: string) => Promise<void>
    signUpWithEmail: (
        email: string,
        password: string
    ) => Promise<{ sessionCreated: boolean }>
    checkUserExists: (email: string) => Promise<boolean>
    signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
    children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [loading, setLoading] = useState(true)
    const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)

    const supabase = getSupabaseClient()

    const getAuthCallbackUrl = (): string => {
        // In dev we must redirect back to the current origin, not a hard-coded prod host.
        // Supabase requires this URL to be allow-listed in Auth settings.
        return new URL('/auth/callback', window.location.origin).toString()
    }

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)

            // Identify user if session exists
            if (session?.user) {
                analytics.identify(session.user.id, {
                    email: session.user.email,
                })
                // Set Sentry user context
                Sentry.setUser({
                    id: session.user.id,
                    email: session.user.email,
                })
                // Set Azure Application Insights user context
                telemetry.setUser(session.user.id)
            }
        })

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)

            // Track authentication events
            if (event === 'SIGNED_IN' && session?.user) {
                analytics.identify(session.user.id, {
                    email: session.user.email,
                })
                analytics.track('user_logged_in', {
                    login_method: 'supabase',
                })
                // Set Sentry user context
                Sentry.setUser({
                    id: session.user.id,
                    email: session.user.email,
                })
                // Set Azure Application Insights user context
                telemetry.setUser(session.user.id)
            } else if (event === 'SIGNED_OUT') {
                analytics.track('user_logged_out')
                analytics.reset()
                // Clear Sentry user context
                Sentry.setUser(null)
                // Clear Azure Application Insights user context
                telemetry.clearUser()
            }
        })

        return () => subscription.unsubscribe()
    }, [supabase])

    // Listen for session expiry events from HTTP interceptor
    useEffect(() => {
        const handleSessionExpired = (event: Event) => {
            const customEvent = event as CustomEvent
            console.log('Session expired event received:', customEvent.detail)
            setShowSessionExpiredModal(true)
        }

        window.addEventListener('auth:session-expired', handleSessionExpired)

        return () => {
            window.removeEventListener('auth:session-expired', handleSessionExpired)
        }
    }, [])

    const signInWithGoogle = async (returnUrl?: string) => {
        // Store returnUrl in sessionStorage to use after OAuth redirect
        if (returnUrl) {
            sessionStorage.setItem('auth_return_url', returnUrl)
        }

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: getAuthCallbackUrl(),
            },
        })
        if (error) {
            console.error('Error signing in with Google:', error)
            throw error
        }
    }

    const signInWithApple = async (returnUrl?: string) => {
        // Store returnUrl in sessionStorage to use after OAuth redirect
        if (returnUrl) {
            sessionStorage.setItem('auth_return_url', returnUrl)
        }

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'apple',
            options: {
                redirectTo: getAuthCallbackUrl(),
            },
        })
        if (error) {
            console.error('Error signing in with Apple:', error)
            throw error
        }
    }

    const signInWithEmail = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        })
        if (error) {
            console.error('Error signing in with email:', error)
            throw error
        }
    }

    const signUpWithEmail = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: getAuthCallbackUrl(),
            },
        })
        if (error) {
            console.error('Error signing up with email:', error)
            throw error
        }

        // Track signup event
        if (data.user) {
            analytics.track('user_signed_up', {
                signup_method: 'email',
            })
        }

        return { sessionCreated: Boolean(data.session) }
    }

    const checkUserExists = async (email: string) => {
        return checkUserEmail(email)
    }

    const signOut = async () => {
        const { error } = await supabase.auth.signOut()
        if (error) {
            console.error('Error signing out:', error)
            throw error
        }
    }

    const handleSessionExpiredModalClose = async () => {
        setShowSessionExpiredModal(false)
        // Sign out and redirect to login
        try {
            await signOut()
        } catch (error) {
            console.error('Error signing out:', error)
        }
        // Redirect to login with reason parameter
        window.location.href = '/login?reason=session_expired'
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                session,
                loading,
                signInWithGoogle,
                signInWithApple,
                signInWithEmail,
                signUpWithEmail,
                checkUserExists,
                signOut,
            }}
        >
            {children}

            {showSessionExpiredModal && (
                <SessionExpiredModal onClose={handleSessionExpiredModalClose} />
            )}
        </AuthContext.Provider>
    )
}

